import { useState } from 'react';
import { ReceiptText } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { cn } from '@/lib/utils';
import NoItems from '@/components/ui/NoItems.tsx';
import { Card } from '@/components/ui/Card.tsx';
import { columnLabelClass } from '@/components/ui/typography.ts';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import { confirmDialogUseHook } from '@/components/ui/ConfirmDialog.useHook.ts';
import { PaymentVm } from '@/features/dashboard/bookingChanges/types/BillViewModel.ts';
import { sumAmounts } from '@/features/dashboard/bookingChanges/utils/money.ts';
import MoneyAmountList from '@/features/dashboard/bookingChanges/components/primitives/MoneyAmountList.tsx';
import CreateFinancialTransactionModal from '@/features/dashboard/financialTransactions/components/CreateFinancialTransactionModal.tsx';
import EditFinancialTransactionModal from '@/features/dashboard/financialTransactions/components/EditFinancialTransactionModal.tsx';
import useDeleteFinancialTransaction from '@/features/dashboard/financialTransactions/hooks/useDeleteFinancialTransaction.ts';
import { bookingsApiQueryKeys } from '@/features/dashboard/bookings/api/bookingsApi.queryKeys.ts';
import PaymentRow from './PaymentRow';
import PaymentTableTotalRow from './PaymentTableTotalRow';
import RecalculateBalanceButton from './RecalculateBalanceButton';
import {
  columnHeaderCellClass,
  paymentsGrid,
  tableCardEmptyClass,
  tableCardHeaderClass,
  tableTotalRowClass,
} from './paymentTableLayout';

/** Karta „Wpłaty" — realne transakcje, każda już zaksięgowana. */
const PaymentsCard = ({
  payments,
  bookingId,
  clientId,
}: {
  payments: PaymentVm[];
  bookingId: string;
  clientId: string;
}) => {
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editedId, setEditedId] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const deleteTransaction = useDeleteFinancialTransaction();
  const { confirmDeleteWithCallback } = confirmDialogUseHook();
  const total = sumAmounts(payments);

  const confirmDelete = (payment: PaymentVm) =>
    confirmDeleteWithCallback({
      title: 'Usuń transakcję',
      description: 'Czy na pewno chcesz usunąć tę transakcję?',
      confirmText: 'Usuń',
      onConfirm: () => deleteTransaction.mutateAsync(payment.id),
    });

  return (
    <Card className="@container overflow-hidden">
      <div className={tableCardHeaderClass}>
        <span className={columnLabelClass}>Wpłaty</span>
        <span className="text-xs font-semibold text-neutral-400">
          realne transakcje
        </span>
        <div className="-my-1 ml-auto flex items-center gap-1 self-center">
          <ActionButton
            tone="outline"
            size="xs"
            onClick={() => setIsCreateOpen(true)}
          >
            <ReceiptText aria-hidden />
            Dodaj transakcję
          </ActionButton>
          <RecalculateBalanceButton clientId={clientId} />
        </div>
      </div>

      {payments.length === 0 ? (
        <div className={tableCardEmptyClass}>
          <NoItems
            title="Brak wpłat"
            description="Żadna transakcja nie została jeszcze przypisana do tej rezerwacji."
          />
        </div>
      ) : (
        <>
          <div role="table" aria-label="Wpłaty">
            <div
              role="row"
              className={cn(
                paymentsGrid,
                '@max-[600px]:hidden border-b border-neutral-100',
              )}
            >
              <div role="columnheader" className={columnHeaderCellClass}>
                Data
              </div>
              <div role="columnheader" className={columnHeaderCellClass}>
                Metoda
              </div>
              <div
                role="columnheader"
                className={cn(columnHeaderCellClass, 'text-right')}
              >
                Kwota
              </div>
              <div role="columnheader" className={columnHeaderCellClass}>
                <span className="sr-only">Akcje</span>
              </div>
            </div>

            {payments.map((payment) => (
              <PaymentRow
                key={payment.id}
                payment={payment}
                bookingId={bookingId}
                onEdit={(row) => setEditedId(row.id)}
                onDelete={confirmDelete}
              />
            ))}
          </div>

          <PaymentTableTotalRow
            className={tableTotalRowClass}
            label="Suma wpłat"
            value={<MoneyAmountList values={total} />}
          />
        </>
      )}

      {isCreateOpen && (
        <CreateFinancialTransactionModal
          clientId={clientId}
          onClose={() => setIsCreateOpen(false)}
        />
      )}

      {editedId && (
        <EditFinancialTransactionModal
          transactionId={editedId}
          onClose={() => {
            setEditedId(null);
            // Tagi zapisują się w modalu od razu, poza mutacją transakcji,
            // więc zamknięcie bez „Zapisz” też musi odświeżyć chipy w wierszach.
            queryClient.invalidateQueries({
              queryKey: bookingsApiQueryKeys.transactions(bookingId),
            });
          }}
        />
      )}
    </Card>
  );
};

export default PaymentsCard;
